/**
 * Activity transformer - converts employee options data into activity events
 */

import { EmployeeOptionsStatement, TransactionType, OptionTransaction } from '../types/employee-statement';

export interface ActivityEvent {
  date: string;
  type: 'Grant' | 'Vesting' | 'Exercise';
  description: string;
  additionalDescription?: string; // Optional second line for description
  shares: number;
  stockPrice: number;
  value: number | null;
  runningGranted: number;
  runningVested: number;
  runningExercised: number;
  // Original data for reference
  transactionType: TransactionType;
  optionAgreementId: number;
  optionAgreementDescription: string;
}

export interface ProcessedActivityData {
  events: ActivityEvent[];
  availableYears: string[];
}

/**
 * Transform employee options data into activity events with running totals
 */
export function transformToActivityEvents(data: EmployeeOptionsStatement): ProcessedActivityData {
  const events: ActivityEvent[] = [];
  const yearsSet = new Set<string>();

  // Activity data mapper puts ALL options in activeOptions.options
  const allAgreements = data.activeOptions.options;

  // Process each option agreement
  allAgreements.forEach(agreement => {
    // Add all transactions from this agreement
    agreement.transactions.forEach(transaction => {
      const transYear = new Date(transaction.effectiveDate).getFullYear().toString();
      yearsSet.add(transYear);

      const event = createEventFromTransaction(transaction, agreement.description, agreement.strikePrice);
      if (event) {
        events.push(event);
      }
    });
  });

  // Sort by date descending (most recent first)
  events.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

  // Calculate running totals (working backwards since we're sorted descending)
  calculateRunningTotals(events);

  return {
    events,
    availableYears: Array.from(yearsSet).sort().reverse()
  };
}

/**
 * Create an activity event from a transaction
 */
function createEventFromTransaction(
  transaction: OptionTransaction,
  optionAgreementDescription: string,
  strikePrice: number
): ActivityEvent | null {
  const baseEvent = {
    date: transaction.effectiveDate,
    stockPrice: transaction.priceAtExercise || 0,
    runningGranted: 0,
    runningVested: 0,
    runningExercised: 0,
    transactionType: transaction.type,
    optionAgreementId: transaction.optionAgreementId,
    optionAgreementDescription
  };

  switch (transaction.type) {
    case TransactionType.GRANT:
      return {
        ...baseEvent,
        type: 'Grant',
        description: optionAgreementDescription,
        additionalDescription: 'Initial Grant',
        shares: transaction.deltaUnvestedOptions, // Delta of unvested shares at grant time
        stockPrice: strikePrice, // Use strike price from agreement, not priceAtExercise
        value: null
      };

    case TransactionType.VEST:
      const vestingDetails = buildVestingDetails(transaction);
      return {
        ...baseEvent,
        type: 'Vesting',
        description: optionAgreementDescription,
        additionalDescription: vestingDetails,
        shares: transaction.deltaVestedOptions, // Delta of vested shares
        value: null
      };

    case TransactionType.CASHLESS_EXERCISE:
      const marketPrice = transaction.priceAtExercise || 0;
      const priceDifference = marketPrice - strikePrice;
      const cashlessDescription = `Cashless Exercise, $${priceDifference.toFixed(2)}/share\n($${marketPrice.toFixed(2)} market - $${strikePrice.toFixed(2)} strike)`;
      return {
        ...baseEvent,
        type: 'Exercise',
        description: optionAgreementDescription,
        additionalDescription: cashlessDescription,
        shares: transaction.deltaCashlessExercised, // Delta of cashless exercised shares
        value: transaction.netValue
      };

    case TransactionType.PURCHASE_EXERCISE:
      return {
        ...baseEvent,
        type: 'Exercise',
        description: optionAgreementDescription,
        additionalDescription: 'Stock purchase',
        shares: transaction.deltaPurchaseExercised, // Delta of purchase exercised shares
        value: transaction.netValue
      };

    default:
      return null;
  }
}

/**
 * Build the additional vesting details (without the agreement name)
 */
function buildVestingDetails(transaction: OptionTransaction): string {
  if (transaction.vestPercentage !== undefined && transaction.vestAnniversary !== undefined) {
    // VestPercentage comes from DB as decimal (e.g., 0.25), convert to percentage
    const percentage = Math.round(transaction.vestPercentage * 100);
    return `Year ${transaction.vestAnniversary} vesting (${percentage}% complete)`;
  }
  return 'Vesting';
}

/**
 * Calculate running totals for all events
 * Events should be sorted by date descending before calling this
 */
function calculateRunningTotals(events: ActivityEvent[]): void {
  let totalGranted = 0;
  let totalVested = 0;
  let totalExercised = 0;

  // Work backwards through the events (from oldest to newest)
  for (let i = events.length - 1; i >= 0; i--) {
    const event = events[i];

    // Update totals based on event type
    switch (event.transactionType) {
      case TransactionType.GRANT:
        totalGranted += event.shares;
        break;
      case TransactionType.VEST:
        totalVested += event.shares;
        break;
      case TransactionType.CASHLESS_EXERCISE:
      case TransactionType.PURCHASE_EXERCISE:
        totalExercised += event.shares;
        break;
    }

    // Store the running totals
    event.runningGranted = totalGranted;
    event.runningVested = totalVested;
    event.runningExercised = totalExercised;
  }
}

/**
 * Filter events by year
 */
export function filterEventsByYear(events: ActivityEvent[], year: string): ActivityEvent[] {
  if (year === 'all') {
    return events;
  }
  return events.filter(event => {
    const eventYear = new Date(event.date).getFullYear().toString();
    return eventYear === year;
  });
}

/**
 * Filter events by type
 */
export function filterEventsByType(events: ActivityEvent[], type: string): ActivityEvent[] {
  if (type === 'all') {
    return events;
  }
  const typeMap: { [key: string]: string } = {
    'grant': 'Grant',
    'vesting': 'Vesting',
    'exercise': 'Exercise'
  };
  const targetType = typeMap[type];
  return events.filter(event => event.type === targetType);
}
